import {
  SpacingProps,
  composeRestyleFunctions,
  spacing,
  useRestyle,
  TypographyProps,
  typography,
  color,
  ColorProps,
  backgroundColor,
  BackgroundColorProps,
  border,
  BorderProps,
  LayoutProps,
  layout,
} from "@shopify/restyle";
import React, { useCallback } from "react";
import { Platform, TextInput } from "react-native";

import { Theme } from "../../../theme/restyle/theme";
import { getInputTypographyStyle } from "./inputTypography";
import { BaseInputProps, InputType, OnChangeType } from "./types";

type RestyleTypography = TypographyProps<Theme> & {
  textAlign?: "left" | "right" | "center";
  textAlignVertical?: "top" | "bottom" | "center";
  verticalAlign?: "top" | "bottom" | "auto" | "middle" | undefined;
};

type RestyleProps = SpacingProps<Theme> &
  RestyleTypography &
  ColorProps<Theme> &
  BackgroundColorProps<Theme> &
  BorderProps<Theme> &
  LayoutProps<Theme>;

export type PrimativeInputProps<T extends InputType> = RestyleProps &
  BaseInputProps<T>;

const restyleFunctions = composeRestyleFunctions<Theme, RestyleProps>([
  spacing,
  typography,
  color,
  backgroundColor,
  border,
  layout,
]);

const webInputOutlineReset = {
  outlineColor: "transparent",
  outlineWidth: 0,
  outline: "none",
};

export const Input = <T extends InputType>({
  testID,
  placeholder,
  value,
  type,
  inputRef,
  onChange,
  name,
  accessibilityLabel,
  placeholderTextColor,
  keyboardType,
  inputMode,
  maxLength,
  ...restProps
}: PrimativeInputProps<T>) => {
  const memoizedOnChange = useCallback(
    (val: string | number) => {
      if (type === "password" || type === "text" || !type) {
        const stringOnChange = onChange as OnChangeType<"text">;
        typeof val === "string"
          ? stringOnChange(val)
          : stringOnChange(val.toString());
      } else if (type === "number") {
        const numberOnChange = onChange as OnChangeType<"number">;
        typeof val === "number"
          ? numberOnChange(val)
          : numberOnChange(isNaN(parseInt(val)) ? 0 : parseInt(val));
      }
    },
    [onChange, type]
  );

  const restyleProps = useRestyle(restyleFunctions, { ...restProps });
  const inputTypographyStyle = getInputTypographyStyle(restyleProps.style);
  const platformProps =
    Platform.OS === "web"
      ? {
          style: [
            restyleProps.style,
            webInputOutlineReset,
            inputTypographyStyle,
          ],
        }
      : Platform.OS === "android"
      ? {
          style: [restyleProps.style, inputTypographyStyle],
          underlineColorAndroid: "transparent",
        }
      : { style: [restyleProps.style, inputTypographyStyle] };

  return (
    <TextInput
      testID={testID}
      placeholder={placeholder}
      value={value?.toString() ?? ""}
      onChangeText={memoizedOnChange}
      placeholderTextColor={placeholderTextColor}
      inputMode={inputMode}
      maxLength={maxLength}
      aria-label={accessibilityLabel || name || undefined}
      ref={inputRef}
      keyboardType={keyboardType}
      {...restyleProps}
      {...platformProps}
    />
  );
};

export * from "./types";
